import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { finalize, Subject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { EExamKey } from '@gray/domain/enums/exam-key.enum';
import { IExam } from '@gray/domain/models/exam/exam.model';
import { examShortName } from '@gray/domain/helpers/exam.helper';
import { CoachMetricsService } from '../../core/services/coach-metrics.service';
import { CoachCatalogService } from '../../core/services/coach-catalog.service';
import { CoachStateService } from '../../core/services/coach-state.service';
import {
  ICoachMetricsEvaluations,
  ICoachMetricsMonth,
  ICoachMetricsStudent,
  ICoachMetricsSummary,
} from '../../core/models/coach-metrics.model';

/** Windows the API accepts; the chips offer exactly these. */
const MONTH_OPTIONS = [3, 6, 12];
const CHART_HEIGHT = 260;

/**
 * How the coach is doing with the group they carry: what their students gained, how much of the
 * plan they finish, how many meetings were held and how close the group is to its goals.
 *
 * Every figure is read over a whole-month window and for one exam, so the two controls in the
 * header reload all four requests. Each section keeps its own loading flag and its skeleton is the
 * height of the real thing — switching window or exam must not make the page jump.
 */
@Component({
  selector: 'coach-metrics',
  templateUrl: './metrics.component.html',
  styleUrl: './metrics.component.scss',
})
export class MetricsComponent implements OnInit, OnDestroy {
  @ViewChild('scoreChartHost') scoreChartHost?: ElementRef<HTMLElement>;
  @ViewChild('meetingChartHost') meetingChartHost?: ElementRef<HTMLElement>;

  summary?: ICoachMetricsSummary;
  monthly: ICoachMetricsMonth[] = [];
  students: ICoachMetricsStudent[] = [];
  evaluations?: ICoachMetricsEvaluations;
  exams: IExam[] = [];

  examKey = EExamKey.TYT;
  examKeys = EExamKey;
  months = 6;
  monthOptions = MONTH_OPTIONS;

  summaryIsLoading = true;
  monthlyIsLoading = true;
  studentsIsLoading = true;
  evaluationsIsLoading = true;

  destroyed$ = new Subject();

  private scoreChart?: { render: () => void; destroy: () => void };
  private meetingChart?: { render: () => void; destroy: () => void };

  constructor(
    private metricsService: CoachMetricsService,
    private catalogService: CoachCatalogService,
    private coachState: CoachStateService,
    private translate: TranslateService,
  ) {
  }

  ngOnInit(): void {
    this.getExams();
    this.getEvaluations();
    this.reload();
  }

  coachName(): string | null {
    return this.coachState.currentCoach?.name ?? null;
  }

  /** Short form, as everywhere else in the panel: TYT / AYT. */
  examName(examKey: EExamKey): string {
    return examShortName(this.exams.find(exam => exam.examKey === examKey), examKey);
  }

  setExamKey(examKey: EExamKey) {
    if (this.examKey === examKey) {
      return;
    }

    this.examKey = examKey;
    this.reload();
  }

  setMonths(months: number) {
    if (this.months === months) {
      return;
    }

    this.months = months;
    this.reload();
  }

  reload() {
    this.getSummary();
    this.getMonthly();
    this.getStudents();
  }

  getExams() {
    this.catalogService.getExams()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(r => this.exams = (r.data?.result ?? r.data ?? []) as IExam[]);
  }

  getSummary() {
    this.summaryIsLoading = true;
    this.metricsService.getSummary(this.examKey, this.months)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.summaryIsLoading = false))
      .subscribe(r => this.summary = r.data as ICoachMetricsSummary);
  }

  getMonthly() {
    this.monthlyIsLoading = true;
    this.metricsService.getMonthly(this.examKey, this.months)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.monthlyIsLoading = false))
      .subscribe(r => {
        this.monthly = (r.data?.result ?? []) as ICoachMetricsMonth[];
        // The chart hosts only exist once the skeletons are gone.
        setTimeout(() => this.renderCharts());
      });
  }

  getStudents() {
    this.studentsIsLoading = true;
    this.metricsService.getStudents(this.examKey, this.months)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.studentsIsLoading = false))
      .subscribe(r => this.students = (r.data?.result ?? []) as ICoachMetricsStudent[]);
  }

  /** Independent of the window and the exam, so it is fetched once. */
  getEvaluations() {
    this.evaluationsIsLoading = true;
    this.metricsService.getEvaluations()
      .pipe(takeUntil(this.destroyed$), finalize(() => this.evaluationsIsLoading = false))
      .subscribe(r => this.evaluations = r.data as ICoachMetricsEvaluations);
  }

  hasScoreSeries(): boolean {
    return this.monthly.some(month => month.averageScore !== null);
  }

  hasMeetingSeries(): boolean {
    return this.monthly.some(month => month.meetingCount > 0);
  }

  /** "2026-06" as the short month name the charts and the header label use. */
  monthLabel(month: string): string {
    const [year, monthNumber] = month.split('-').map(Number);
    return new Date(year, monthNumber - 1, 1)
      .toLocaleDateString('tr-TR', {month: 'short'});
  }

  // region Charts
  private async renderCharts() {
    const module = await import('apexcharts');
    const ApexCharts = module.default;

    this.scoreChart?.destroy();
    this.scoreChart = undefined;
    this.meetingChart?.destroy();
    this.meetingChart = undefined;

    const scoreHost = this.scoreChartHost?.nativeElement;
    if (scoreHost && this.hasScoreSeries()) {
      this.scoreChart = new ApexCharts(scoreHost, this.buildScoreOptions());
      this.scoreChart.render();
    }

    const meetingHost = this.meetingChartHost?.nativeElement;
    if (meetingHost && this.hasMeetingSeries()) {
      this.meetingChart = new ApexCharts(meetingHost, this.buildMeetingOptions());
      this.meetingChart.render();
    }
  }

  private buildScoreOptions() {
    const accent = this.cssValue('--brand-primary', '#FFD200');

    return {
      ...this.baseOptions('area'),
      series: [{
        name: this.translate.instant('metrics.average_net'),
        // A month nobody sat an exam in is a gap, not a zero.
        data: this.monthly.map(month => month.averageScore),
      }],
      colors: [accent],
      stroke: {curve: 'smooth', width: 3},
      fill: {
        type: 'gradient',
        gradient: {shadeIntensity: 1, opacityFrom: .35, opacityTo: 0, stops: [0, 100]},
      },
      markers: {size: 4, colors: [accent], strokeWidth: 0},
    };
  }

  private buildMeetingOptions() {
    const accent = this.cssValue('--brand-primary', '#FFD200');

    return {
      ...this.baseOptions('bar'),
      series: [{
        name: this.translate.instant('metrics.meeting_count'),
        data: this.monthly.map(month => month.meetingCount),
      }],
      colors: [accent],
      plotOptions: {bar: {borderRadius: 6, columnWidth: '45%'}},
    };
  }

  private baseOptions(type: 'area' | 'bar') {
    const grid = this.cssValue('--gray-100', '#E7E7E7');
    const muted = this.cssValue('--gray-400', '#888888');

    return {
      chart: {
        type,
        height: CHART_HEIGHT,
        toolbar: {show: false},
        zoom: {enabled: false},
        fontFamily: 'inherit',
        parentHeightOffset: 0,
      },
      dataLabels: {enabled: false},
      grid: {
        borderColor: grid,
        strokeDashArray: 4,
        xaxis: {lines: {show: false}},
        padding: {left: 4, right: 4},
      },
      xaxis: {
        categories: this.monthly.map(month => this.monthLabel(month.month)),
        axisBorder: {show: false},
        axisTicks: {show: false},
        labels: {style: {colors: muted, fontSize: '11px', fontWeight: 600}},
        tooltip: {enabled: false},
      },
      yaxis: {
        labels: {
          style: {colors: muted, fontSize: '11px'},
          formatter: (value: number) => `${Math.round(value)}`,
        },
      },
      tooltip: {theme: 'light'},
    };
  }

  private cssValue(name: string, fallback: string): string {
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return value || fallback;
  }
  // endregion

  ngOnDestroy(): void {
    this.scoreChart?.destroy();
    this.meetingChart?.destroy();
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
